/**
 * ChatPanel Component
 *
 * Main container for an agent chat session.
 * Combines MessageList, ChatInput, StatusBar, and PermissionDialog.
 *
 * @module components/session/ChatPanel
 */

import { memo, useCallback, useMemo, useRef, useEffect, useState } from 'react';
import { MessageList } from './MessageList.jsx';
import SessionChatInput from './SessionChatInput.jsx';
import StatusBar from './StatusBar.jsx';
import { FileButtons } from './FileButtons.jsx';
import { DiffPopout } from './DiffPopout.jsx';
import { useAgentChat, useAgentSessionData } from '../../contexts/AgentChatContext.jsx';
import { useAgentMessages } from '../../contexts/MessagesContext.jsx';
import { useUI } from '../../contexts/UIContext.jsx';
import { useAuth } from '../../contexts/AuthContext.jsx';
import useUIStore from '../../stores/uiStore.js';
import { useChangedFiles } from '../../hooks/useChangedFiles.js';

// ============================================
// Permission Dialog
// ============================================

function PermissionDialog({ permission, onAllow, onDeny }) {
  if (!permission) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-lg max-w-md w-full p-6 shadow-xl border border-gray-700">
        <h3 className="text-lg font-semibold text-gray-100 mb-2">Permission Request</h3>
        <div className="text-gray-400 mb-4">
          <p className="mb-2">
            <span className="text-indigo-400 font-medium">{permission.tool}</span> wants to:
          </p>
          <p className="text-gray-300">{permission.description}</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => onDeny(permission.requestId)}
            className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg transition-colors"
          >
            Deny
          </button>
          <button
            onClick={() => onAllow(permission.requestId, 'once')}
            className="flex-1 px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg transition-colors"
          >
            Allow Once
          </button>
          <button
            onClick={() => onAllow(permission.requestId, 'session')}
            className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-500 text-white rounded-lg transition-colors"
          >
            Allow Session
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================
// Stable Memoization Hook
// ============================================

function useMemoizedSession(sessionData) {
  const stableRef = useRef({});
  const currentRef = stableRef.current;

  // Only update stable ref for properties that should trigger re-render
  if (currentRef.active !== sessionData?.active) currentRef.active = sessionData?.active;
  if (currentRef.connected !== sessionData?.connected) currentRef.connected = sessionData?.connected;
  if (currentRef.isProcessing !== sessionData?.isProcessing) currentRef.isProcessing = sessionData?.isProcessing;
  if (currentRef.permissionMode !== sessionData?.permissionMode) currentRef.permissionMode = sessionData?.permissionMode;
  if (currentRef.controlInitialized !== sessionData?.controlInitialized) currentRef.controlInitialized = sessionData?.controlInitialized;
  if (currentRef.pendingPermission !== sessionData?.pendingPermission) currentRef.pendingPermission = sessionData?.pendingPermission;
  if (currentRef.availableCommands !== sessionData?.availableCommands) currentRef.availableCommands = sessionData?.availableCommands;

  // Stats are updated frequently, compare by value
  const stats = sessionData?.stats;
  if (stats) {
    if (!currentRef.stats ||
        currentRef.stats.contextLeftPercent !== stats.contextLeftPercent ||
        currentRef.stats.totalCost !== stats.totalCost ||
        currentRef.stats.numTurns !== stats.numTurns ||
        currentRef.stats.isCompacting !== stats.isCompacting) {
      currentRef.stats = { ...stats };
    }
  }

  return currentRef;
}

// ============================================
// Main ChatPanel Component
// ============================================

// Note: No custom comparator - hooks inside (useAgentMessages, useChangedFiles)
// need to re-run when context changes. memo() alone provides reference equality check.
export const ChatPanel = memo(function ChatPanel({ agentId, agent }) {
  const sessionData = useAgentSessionData(agentId);
  const messages = useAgentMessages(agentId);
  const { sendMessage, stopGeneration, changePermissionMode, changeModel, respondToPermission, markAsRead } = useAgentChat();
  const { focusedAgentId, splitSessions, saveScrollPosition } = useUI();
  const { user } = useAuth();

  // Raw mode state from uiStore (debug JSON view)
  const rawModeEnabled = useUIStore((state) => state.rawModeAgents[agentId] || false);
  const toggleRawMode = useUIStore((state) => state.toggleRawMode);

  // Self-Note settings from agent prop, with local state for optimistic updates
  const [selfNoteEnabled, setSelfNoteEnabled] = useState(agent?.selfNoteEnabled ?? false);
  const [selfNoteThreshold, setSelfNoteThreshold] = useState(agent?.selfNoteThreshold ?? 74);

  // Whisper mode from agent prop
  const [whisperEnabled, setWhisperEnabled] = useState(agent?.whisperEnabled ?? true);

  // Sync with agent prop when it changes
  useEffect(() => {
    if (agent?.selfNoteEnabled !== undefined) setSelfNoteEnabled(agent.selfNoteEnabled);
    if (agent?.selfNoteThreshold !== undefined) setSelfNoteThreshold(agent.selfNoteThreshold);
    if (agent?.whisperEnabled !== undefined) setWhisperEnabled(agent.whisperEnabled);
  }, [agent?.selfNoteEnabled, agent?.selfNoteThreshold, agent?.whisperEnabled]);

  // Stable session reference to prevent MessageList re-renders during streaming
  const stableSession = useMemoizedSession(sessionData);

  // Changed files detection for FileView feature (PRD-UI-001)
  const changedFiles = useChangedFiles(messages);
  const [loadingFile, setLoadingFile] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileError, setFileError] = useState(null);

  // Mark as read when focused
  const isFocused = agentId === focusedAgentId || splitSessions.includes(agentId);
  useEffect(() => {
    if (isFocused && sessionData?.unreadCount > 0) {
      markAsRead(agentId);
    }
  }, [isFocused, sessionData?.unreadCount, agentId, markAsRead]);

  // Handlers
  const handleSendMessage = useCallback((message, attachments) => {
    sendMessage(agentId, message, attachments);
  }, [agentId, sendMessage]);

  const handleStopGeneration = useCallback(() => {
    stopGeneration(agentId);
  }, [agentId, stopGeneration]);

  const handleChangePermissionMode = useCallback((mode) => {
    changePermissionMode(agentId, mode);
  }, [agentId, changePermissionMode]);

  const handleChangeModel = useCallback((model) => {
    changeModel(agentId, model);
  }, [agentId, changeModel]);

  const handleAllowPermission = useCallback((requestId, scope) => {
    respondToPermission(agentId, requestId, true, scope);
  }, [agentId, respondToPermission]);

  const handleDenyPermission = useCallback((requestId) => {
    respondToPermission(agentId, requestId, false);
  }, [agentId, respondToPermission]);

  // Handle scroll position save
  const handleScrollPositionChange = useCallback((scrollTop) => {
    saveScrollPosition(agentId, scrollTop);
  }, [agentId, saveScrollPosition]);

  // Handle raw mode toggle
  const handleToggleRawMode = useCallback(() => {
    toggleRawMode(agentId);
  }, [agentId, toggleRawMode]);

  // Handle file button click - fetch diff and open popout (PRD-UI-001)
  // Default to git-last-commit mode - shows what changed in the agent's last commit
  const handleFileClick = useCallback(async (file) => {
    setLoadingFile(file.path);
    setFileError(null);
    try {
      const response = await fetch(
        `/api/agents/${agentId}/files${file.path}?mode=git-last-commit`,
        { credentials: 'include' }
      );
      if (!response.ok) {
        const errorMsg = response.status === 404
          ? `File not found: ${file.fileName}`
          : `Failed to load file (${response.status})`;
        setFileError(errorMsg);
        // Auto-clear error after 5 seconds
        setTimeout(() => setFileError(null), 5000);
        return;
      }
      const data = await response.json();
      setSelectedFile({ ...file, ...data });
    } catch (err) {
      console.error('Failed to fetch file:', err);
      setFileError('Network error - could not load file');
      setTimeout(() => setFileError(null), 5000);
    } finally {
      setLoadingFile(null);
    }
  }, [agentId]);

  // Close diff popout
  const handleCloseDiff = useCallback(() => {
    setSelectedFile(null);
  }, []);

  // Handle diff mode change (refetch with new mode)
  const handleDiffModeChange = useCallback(async (mode) => {
    if (!selectedFile) return;
    setLoadingFile(selectedFile.path);
    try {
      const response = await fetch(
        `/api/agents/${agentId}/files${selectedFile.path}?mode=${mode}`,
        { credentials: 'include' }
      );
      if (!response.ok) {
        console.error('Failed to fetch file with mode:', mode);
        return;
      }
      const data = await response.json();
      setSelectedFile(prev => ({ ...prev, ...data }));
    } catch (err) {
      console.error('Failed to fetch file:', err);
    } finally {
      setLoadingFile(null);
    }
  }, [agentId, selectedFile]);

  // Self-Note API handlers
  const handleToggleSelfNote = useCallback(async (enabled) => {
    // Optimistic update
    setSelfNoteEnabled(enabled);
    try {
      const response = await fetch(`/api/agents/${agentId}/self-note-settings`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ selfNoteEnabled: enabled }),
      });
      if (!response.ok) {
        // Revert on error
        setSelfNoteEnabled(!enabled);
        console.error('Failed to update self-note setting');
      }
    } catch (err) {
      setSelfNoteEnabled(!enabled);
      console.error('Failed to update self-note setting:', err);
    }
  }, [agentId]);

  const handleChangeSelfNoteThreshold = useCallback(async (threshold) => {
    const prevThreshold = selfNoteThreshold;
    // Optimistic update
    setSelfNoteThreshold(threshold);
    try {
      const response = await fetch(`/api/agents/${agentId}/self-note-settings`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ selfNoteThreshold: threshold }),
      });
      if (!response.ok) {
        setSelfNoteThreshold(prevThreshold);
        console.error('Failed to update self-note threshold');
      }
    } catch (err) {
      setSelfNoteThreshold(prevThreshold);
      console.error('Failed to update self-note threshold:', err);
    }
  }, [agentId, selfNoteThreshold]);

  // Whisper mode handler - maps StatusBar mode to API enabled boolean
  const handleChangeWhisperMode = useCallback(async (mode) => {
    // mode: 'enabled' | 'disabled' | 'focus'
    // For now, 'enabled' = true, 'disabled'/'focus' = false for persistent setting
    const enabled = mode === 'enabled';
    const prevEnabled = whisperEnabled;
    // Optimistic update
    setWhisperEnabled(enabled);
    try {
      const response = await fetch(`/api/agents/${agentId}/whisper-mode`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ enabled }),
      });
      if (!response.ok) {
        setWhisperEnabled(prevEnabled);
        console.error('Failed to update whisper mode');
      }
    } catch (err) {
      setWhisperEnabled(prevEnabled);
      console.error('Failed to update whisper mode:', err);
    }
  }, [agentId, whisperEnabled]);

  // Determine display state
  const isConnected = stableSession.connected ?? false;
  const isActive = stableSession.active ?? false;
  const isProcessing = stableSession.isProcessing ?? false;

  // Agent info
  const agentName = agent?.displayName || agent?.name || 'Agent';
  const agentAvatar = agent?.avatarUrl || null;
  const userAvatar = user?.avatarUrl || null;

  return (
    <div className="relative flex flex-col h-full bg-gray-900">
      {/* Permission Dialog */}
      <PermissionDialog
        permission={stableSession.pendingPermission}
        onAllow={handleAllowPermission}
        onDeny={handleDenyPermission}
      />

      {/* Diff Popout (PRD-UI-001) */}
      {selectedFile && (
        <DiffPopout
          file={selectedFile}
          onClose={handleCloseDiff}
          onModeChange={handleDiffModeChange}
          isLoading={loadingFile === selectedFile.path}
          agentId={agentId}
        />
      )}

      {/* File Buttons - absolute within this ChatPanel (PRD-UI-001) */}
      {changedFiles.length > 0 && (
        <FileButtons
          changedFiles={changedFiles}
          onFileClick={handleFileClick}
          loadingFile={loadingFile}
          agentId={agentId}
          className="absolute top-2 right-2 z-20"
        />
      )}

      {/* File Error Banner */}
      {fileError && (
        <div className="absolute top-14 right-2 z-20 px-3 py-2 bg-red-900/90 border border-red-700 rounded-md text-sm text-red-200 backdrop-blur-sm animate-pulse">
          {fileError}
        </div>
      )}

      {/* Message List */}
      <MessageList
        messages={messages}
        isProcessing={isProcessing}
        onSendMessage={handleSendMessage}
        agentId={agentId}
        agentName={agentName}
        agentAvatar={agentAvatar}
        userAvatar={userAvatar}
        onScrollPositionChange={handleScrollPositionChange}
        rawModeEnabled={rawModeEnabled}
      />

      {/* Status Bar */}
      <StatusBar
        sessionStats={stableSession.stats}
        isProcessing={isProcessing}
        connected={isConnected}
        sessionStatus={stableSession.status}
        permissionMode={stableSession.permissionMode}
        controlInitialized={stableSession.controlInitialized}
        onChangeMode={handleChangePermissionMode}
        currentModel={stableSession.currentModel || agent?.model}
        onChangeModel={handleChangeModel}
        selfNoteEnabled={selfNoteEnabled}
        selfNoteThreshold={selfNoteThreshold}
        onToggleSelfNote={handleToggleSelfNote}
        onChangeSelfNoteThreshold={handleChangeSelfNoteThreshold}
        rawModeEnabled={rawModeEnabled}
        onToggleRawMode={handleToggleRawMode}
        whisperMode={whisperEnabled ? 'enabled' : 'disabled'}
        onChangeWhisperMode={handleChangeWhisperMode}
      />

      {/* Chat Input */}
      <SessionChatInput
        onSend={handleSendMessage}
        onStop={handleStopGeneration}
        disabled={!isActive}
        isProcessing={isProcessing}
        placeholder={isActive ? 'Type a message...' : 'Session not active'}
        agentId={agentId}
        availableCommands={stableSession.availableCommands}
      />
    </div>
  );
});

export default ChatPanel;
